import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { MultiSelectFilter } from '../../../../components/ui/MultiSelectFilter';
import { firebaseAuth } from '../../../../lib/firebase';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { ApiError } from '../../../../lib/api';
import { resolvePortalReportConfig } from '../../../../lib/portalReport/config';
import type {
  PortalCampaignOption,
  PortalGoogleReport,
  PortalMetaReport,
  PortalOverviewReport,
} from '../../../../types/portalReport';
import type { PortalWebsiteReport } from '../../../../types/portalWebsite';
import { resolvePortalNavSelection } from '../../../../lib/portalReport/locationSelection';
import { carryAcrossTabChange } from '../../../../lib/portalReport/reportUrlState';
import {
  pickDatesNote,
  resolveReportView,
} from '../../../../lib/portalReport/reportView';
import { PortalShell } from '../PortalShell';
import { usePortalMode } from '../PortalModeContext';
import { PeriodControl } from './PeriodControl';
import { LocationPicker } from './LocationPicker';
import { SelectField } from '../../../../components/ui/SelectField';
import { PORTAL_COMPARISON_MODES } from '../../../../lib/portalReport/comparison';
import { ChannelBlock } from './ChannelBlock';
import { ReportLayout } from './ReportLayout';
import { ReportSections, type SectionRenderers } from './ReportSections';
import {
  fetchPortalGoogleReport,
  fetchPortalMetaReport,
  fetchPortalOverview,
  fetchPortalWebsiteReport,
} from './reportApi';
import {
  ByLocationTable,
  CampaignsTable,
  CreativesGrid,
  CrmSection,
  SearchTermsTable,
} from './tables';
import { todayLocal, useReportState, type ReportState } from './useReportState';
// ⚠️⚠️ THE RESTYLE, BEHIND A PER-ACCOUNT FLAG. `report.restyledReport` is
// resolved SERVER-SIDE from the scope and defaults to FALSE, so every account
// keeps the existing ReportLayout / ChannelBlock path below until an admin
// turns it on. That is what makes a rollback a toggle rather than a revert and
// a deploy — see src/lib/portalReport/restyleFlag.ts.
import { RestyledReport, type ReportTab } from './restyle';

// CLIENT PORTAL Phase 1 — the three report pages.
//
// ⚠️ The section ORDER comes from the per-brand config, resolved here from the
// labels the server already sent. v1 has one config, which is precisely why the
// indirection has to exist now: hardcoding the order into three pages costs
// nothing today and costs a rewrite the first time a brand's report differs.

/**
 * ⚠️ A 403 means the client does not have the `reporting` permission — a
 * legitimate configuration, not an error. Scott: *"not all users will get
 * reports."* It says so plainly and points at a human; rendering a crash page
 * would make a deliberate restriction look like a fault.
 */
function ReportError({ error }: { error: unknown }) {
  const signedInAs = firebaseAuth.currentUser?.email ?? null;
  if (error instanceof ApiError && error.status === 403) {
    return (
      <PortalShell signedInAs={signedInAs}>
        <div className="mx-auto max-w-md text-center">
          <h1 className="text-xl font-semibold text-foreground">Reports aren't enabled</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account doesn't include reporting. Contact your Kyck account manager if you were
            expecting to see it here.
          </p>
        </div>
      </PortalShell>
    );
  }
  return (
    <PortalShell signedInAs={signedInAs}>
      <div className="mx-auto max-w-md text-center">
        <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We couldn't load your report. Try again in a moment.
        </p>
        <Button variant="outline" className="mt-6" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    </PortalShell>
  );
}

/**
 * Render the restyled report for a client whose account has it turned on.
 *
 * ⚠️ The three portal pages are ROUTES, so the restyle's tab switch navigates
 * rather than swapping local state — otherwise a client on the Meta tab would
 * have a URL that says Overview, and a refresh would silently move them.
 *
 * ⚠️⚠️ IT NOW RENDERS INSIDE `PortalShell` (2026-09-02), which it used to
 * replace. The shell gained the portal's LEFT NAV and the ONE light/dark
 * control, and both have to be present on the report — a nav that vanishes on
 * the page a client spends their time on is not a nav, and a mode toggle that
 * lives in two places is two ideas of which is authoritative.
 *
 * ⚠️ What stopped it nesting before was two headers. It still has its own,
 * carrying the club name, the account manager, the period note, the campaign
 * filter and the report tabs — all of which belong to the report. What moved UP
 * to the shell is the pair that belonged to the portal: the brand lockup and the
 * mode toggle. `RestyledReport` drops both when a `mode` prop is supplied; the
 * staff preview supplies none and is untouched.
 *
 * ⚠️ `bleed` so the report keeps its own full-bleed bands and its own
 * `rpt-shell` measure rather than sitting inside the shell's reading column.
 */
function Restyled({
  tab,
  overview,
  meta,
  google,
  website,
  onStudioChange,
  state,
  signedInAs,
  refreshing,
}: {
  tab: ReportTab;
  overview?: PortalOverviewReport;
  meta?: PortalMetaReport;
  google?: PortalGoogleReport;
  website?: PortalWebsiteReport;
  onStudioChange?: (studio: string | null) => void;
  state: ReportState;
  signedInAs: string | null;
  /**
   * A new report is in flight while this one is on screen (§8).
   *
   * ⚠️⚠️ REQUIRED, NOT OPTIONAL. Three pages render this component, and an
   * optional flag would let one of them keep the silent behaviour with nothing
   * to notice — which is precisely how round 7 §2's cost went unnoticed in the
   * first place.
   */
  refreshing: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mode, toggle } = usePortalMode();
  const report = overview ?? meta ?? google ?? website;
  if (!report) return null;
  const config = resolvePortalReportConfig(report.brandAccountId, report.brandName);
  const options = meta?.campaignOptions ?? google?.campaignOptions ?? [];
  // ⚠️⚠️ RESOLVED ONCE, HERE, AND HANDED TO BOTH SURFACES THAT NEED IT — the
  // report's heading and the left nav's active row. Two answers to "which club
  // is showing" is exactly the defect §B fixes: the heading used to derive its
  // own from the `accounts` URL parameter and printed "All 6 locations" over a
  // nav drill-down, which writes `account`. The server owns the precedence and
  // ships the answer; this is the one place it is read.
  const navSelection = resolvePortalNavSelection(report.selectedAccountId, state.accountIds);
  return (
    <PortalShell
      signedInAs={signedInAs}
      // ⚠️ The nav lists the client's own locations, which is the ONLY route into
      // a single club's report on the restyled path — its tables have no
      // drill-down. It reads the FULL scope from `/me` rather than this report's
      // `accounts`, which is post-narrowing and would collapse to one row the
      // moment a client used it.
      nav
      // ⚠️ The nav needs the SAME answer the heading uses, or the underline and
      // the club name can disagree about what is on screen.
      navSelection={navSelection}
      bleed
    >
    <RestyledReport
      mode={mode}
      onToggleMode={toggle}
      tab={tab}
      onTabChange={(next) => {
        // ⚠️⚠️ IT USED TO NAVIGATE TO A BARE PATH, which threw away the whole
        // search string — so `?account=` went with it and a club report fell
        // back to All locations (Scott's symptom), while the period,
        // comparison and grouping reset because they were local state in a
        // hook that remounts per route. §7 fixed both halves; this is the
        // half that carries what the URL now holds.
        //
        // ⚠️ THROUGH THE ALLOWLIST, never `location.search` verbatim. The
        // allowlist is what keeps a channel-specific parameter from reaching
        // the other channel — see `carryAcrossTabChange`.
        const search = carryAcrossTabChange(searchParams);
        const path = next === 'overview' ? '/portal/report' : `/portal/report/${next}`;
        navigate(`${path}${search}`);
      }}
      overview={overview ?? null}
      meta={meta ?? null}
      google={google ?? null}
      website={website ?? null}
      onStudioChange={onStudioChange}
      config={config}
      // ⚠️ The CLIENT's own local today (it rides `params`), so "this period
      // excludes today" is true for THEM — a report opened in Auckland on the
      // 1st must not describe someone else’s calendar.
      today={state.params?.today ?? todayLocal()}
      // ⚠️ THE CLIENT PATH SUPPLIES THE PICKER; the staff preview does not, and
      // keeps the plain heading — there is no `/me` behind it to list a scope
      // from. See LocationPicker.tsx.
      locationPicker={
        <LocationPicker
          selected={state.accountIds}
          // ⚠️ The payload's answer, via the shared resolver — not a second
          // reading of the URL.
          navSelection={navSelection}
          // ⚠️ THE PARAMETER, not the resolved selection - see the prop's own note.
          navDrillDown={state.navDrillDown}
          onChange={state.setAccountIds}
        />
      }
      controls={<ReportControls state={state} options={options} />}
      // ⚠️ The goal filter is local state (like the campaign filter) so it cannot
      // survive a tab change. The server ignores it for a scope without the
      // goal-split page, so passing it everywhere is harmless.
      goalFilter={{ value: state.goalBucket ?? null, onChange: state.setGoalBucket }}
      // ⚠️ The cue goes NEXT TO THE CONTROLS, where the click happened, and where
      // it can be read at the same time as BOTH halves of the mismatch it explains
      // — see `RestyledReport`.
      refreshing={refreshing}
    />
    </PortalShell>
  );
}

/**
 * The period, comparison and campaign controls (§2, 2026-09-04).
 *
 * ⚠️⚠️ EXTRACTED BECAUSE THEY MUST BE RENDERABLE WITHOUT A REPORT. They were
 * inline in the `controls` slot of a component that needs a payload, so the page
 * that had nothing to draw had nowhere to put them — and "Custom range" with no
 * dates yet is exactly that page. The controls are STATE-driven; the heading,
 * the brand theme and the location picker are REPORT-driven. This is the
 * report-independent half, split out; the rest of the chrome is untouched.
 *
 * ⚠️ ONE DEFINITION, TWO CALL SITES. A second copy for the pick-dates view
 * would be a second idea of what the controls are — and the pre-restyle
 * `ReportLayout` already renders its own set, which is one too many already.
 */
function ReportControls({
  state,
  options,
}: {
  state: ReportState;
  /** Empty on the Overview and on the pick-dates view — the filter is a channel control. */
  options: PortalCampaignOption[];
}) {
  return (
    <>
    {/*
      ⚠️⚠️ THE PERIOD AND COMPARISON CONTROLS WERE ORPHANED, NOT MISSING.
      The server has always accepted `from` / `to` / `grouping` / `today` /
      `comparison`, and `useReportState` has always held every setter — but
      the controls were rendered by `ReportLayout`, the OLD report's frame,
      and went with it when the restyle became the only client report on
      2026-09-01. This is re-surfacing existing wiring.

      ⚠️ THE EXISTING `PeriodControl` and `PORTAL_COMPARISON_MODES`, not
      portal-specific copies. Presets resolve through the SHARED
      `presetToRange` and the comparison window through the AM home's own
      `comparisonRange`, so a client and their account manager cannot end
      up meaning different things by "last month" or "vs last year".

      ⚠️ On EVERY page, unlike the campaign filter below — the period is
      what the whole report is about, so a tab where it vanished would look
      like the tab had its own.
    */}
    <PeriodControl
      preset={state.preset}
      custom={state.custom}
      onPresetChange={state.setPreset}
      onCustomChange={state.setCustom}
    />
    {/*
      ⚠️ NAMES THE MODE, NOT THE DATES. The window is resolved server-side
      through the AM Advertising Home's own `comparisonRange`, so printing
      dates here would be a second statement of the same fact with its own
      chance to be wrong.
    */}
    {/*
      ⚠️⚠️ THE WIDTH IS BOUNDED HERE, AT THE CALL SITE (§3, 2026-09-04).

      `.rpt-controls` is a WRAPPING flex row and `SelectTrigger` carries
      `w-full` from the shared shadcn primitive, so as a DIRECT flex item this
      control's base size was the whole line: it shrank while sharing a row and
      expanded to the full container the moment it wrapped onto its own. The
      Meta and Google tabs add a THIRD control (the campaign filter, rendered
      only where campaigns exist), which is what tipped the row into wrapping —
      so it looked right on the one tab that happened not to.

      ⚠️⚠️ NOT FIXED BY EDITING `select.tsx`. That is the shared primitive behind
      every select in the product, staff CRM included; changing it would resize
      controls on pages nobody in this round has opened.

      ⚠️ THE SAME STRATEGY AS ITS NEIGHBOUR, not a third one: `MultiSelectFilter`
      already bounds itself this way, which is why the campaign filter looked
      right on the same row. Two controls in one row had two different width
      strategies.

      ⚠️⚠️ THIRD DEFECT ON THE SHADCN / `.rpt-*` BOUNDARY, after `.rpt-controls
      button` repainting a white popover's buttons at 1.15:1 and round 6's
      fabricated `rpt-card-head`. A SHARED PRIMITIVE DROPPED INTO A SCOPED
      SURFACE BRINGS ITS OWN WIDTH, COLOUR AND SPECIFICITY, AND THE SCOPED
      SURFACE'S RULES THEN FIGHT IT — so both must be set deliberately here.
    */}
    <SelectField
      className="min-w-[160px] max-w-[280px] truncate"
      aria-label="Compare with"
      value={state.comparison}
      onValueChange={(v) => state.setComparison(v as typeof state.comparison)}
      options={PORTAL_COMPARISON_MODES.map((m) => ({
        value: m.value,
        label: `vs ${m.label.toLowerCase()}`,
      }))}
    />
    {options.length > 0 && (
      // ⚠️ The EXISTING MultiSelectFilter, passed in — Scott asked for the
      // same filter, not a new one, and a second control would be a second
      // idea of what an empty selection means.
      <MultiSelectFilter
        label="Campaigns"
        ariaLabel="Filter by campaign"
        placeholder="All campaigns"
        options={options.map((o) => ({ value: o.campaignId, label: o.campaignName }))}
        selected={state.campaignIds ?? []}
        onChange={(next) => state.setCampaignIds(next.length === 0 ? undefined : next)}
      />
    )}
    </>
  );
}
/**
 * ⚠️⚠️ WAITING FOR THE READER, NOT FOR DATA (§2, 2026-09-04) — and this view
 * existing is the whole fix for the custom-range deadlock.
 *
 * A custom preset with no dates yet cannot be fetched, so `params` is null. All
 * three pages read that as "nothing to show" and rendered a spinner — which
 * removed the `PeriodControl` that was the only thing able to resolve it. This
 * renders the CONTROLS and says what it is waiting for.
 *
 * ⚠️ IT IS REACHED ONLY ON A COLD START. On a warm change of range
 * `placeholderData` keeps the previous report, so the reader keeps their
 * bearings and this never appears. Cold is a forwarded `?preset=custom` link, or
 * a REFRESH mid-selection — which is not exotic, and is why the warm path alone
 * would have left the deadlock reachable.
 *
 * ⚠️ NOT SEEDED. Feeding the calendar a placeholder end makes the second click
 * restart the selection — see `useReportState`'s own note. That would trade one
 * deadlock for another.
 */
function PickDates({ state, signedInAs }: { state: ReportState; signedInAs: string | null }) {
  return (
    <PortalShell signedInAs={signedInAs} nav>
      <div className="rpt-controls mb-6">
        <ReportControls state={state} options={[]} />
      </div>
      <p className="text-sm text-muted-foreground">{pickDatesNote(Boolean(state.custom.from))}</p>
    </PortalShell>
  );
}
function Loading() {
  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null}>
      <CenteredSpinner />
    </PortalShell>
  );
}

/**
 * A client whose scope resolved to nothing. ⚠️ Said plainly and pointed at a
 * human: this is what a revoked or misconfigured grant looks like, and leaving
 * them staring at empty charts would be worse than useless.
 */
function EmptyScope({ state, signedInAs }: { state: ReportState; signedInAs: string | null }) {
  return (
    <ReportLayout state={state} signedInAs={signedInAs} accounts={[]} selectedAccountId={null}>
      <p className="text-sm text-muted-foreground">
        There are no accounts attached to your login yet. Contact your Kyck account manager and
        they'll set it up.
      </p>
    </ReportLayout>
  );
}

// ---------------------------------------------------------------------------

export function ReportOverviewPage() {
  const state = useReportState();
  const signedInAs = firebaseAuth.currentUser?.email ?? null;
  const query = useQuery<PortalOverviewReport>({
    queryKey: ['portal-report-overview', state.params],
    queryFn: () => fetchPortalOverview(state.params!),
    enabled: state.params !== null,
    // ⚠️⚠️ KEEP THE PREVIOUS REPORT WHILE A NEW RANGE IS PICKED (§2). A key
    // change would otherwise empty `query.data`, and the chrome — including the
    // period control — is rendered from the report. Picking "Custom range" makes
    // `params` null, so without this the page has nothing to draw and the control
    // that supplies the dates goes with it.
    //
    // ⚠️ THE HEADER STAYS HONEST FOR FREE: it renders `report.range`, the
    // PAYLOAD's window, so a half-picked range keeps naming the period the
    // numbers actually came from rather than the one being drafted.
    placeholderData: (prev) => prev,
    retry: false,
  });

  // ⚠️⚠️ ONE RULE, ONE PLACE (§2, `resolveReportView`). This was three copies of
  // a guard trio agreeing by coincidence, and the copy that mattered read
  // `params === null` — a sentinel meaning "do not fetch yet" — as "nothing to
  // show", which removed the control the page was waiting for.
  const view = resolveReportView({
    paramsReady: state.params !== null,
    hasReport: Boolean(query.data),
    isLoading: query.isLoading,
    hasError: Boolean(query.error),
    // ⚠️⚠️ THE NARROW PAIR, NOT `isFetching` ALONE (§8). `isPlaceholderData` means
    // "what is on screen came from the PREVIOUS key" — which is exactly the case
    // where the figures and the controls disagree. A same-key background refetch
    // (a window refocus) is fetching too, and flashing a cue over data that is
    // already correct is how a reader learns to ignore it.
    isRefreshing: query.isFetching && query.isPlaceholderData,
  });
  if (view === 'pick-dates') return <PickDates state={state} signedInAs={signedInAs} />;
  if (view === 'loading') return <Loading />;
  if (view === 'error' || !query.data) return <ReportError error={query.error} />;

  const report = query.data;
  if (report.accounts.length === 0) return <EmptyScope state={state} signedInAs={signedInAs} />;
  // ⚠️⚠️ ALWAYS TRUE FOR A NON-EMPTY SCOPE since Phase 1 of the old report's
  // retirement (2026-09-01) — so everything below this line is now UNREACHABLE
  // for a portal user, including `config.overviewSections` and the
  // `ReportSections` renderer map.
  //
  // ⚠️ It is left standing on purpose: this branch plus the resolver's one line
  // IS the revert path. `noUnusedLocals` is off in all three tsconfigs, so
  // nothing flags it and nothing is meant to — Phase 2 removes it deliberately.
  // See src/lib/portalReport/restyleFlag.ts.
  if (report.restyledReport) return <Restyled tab="overview" overview={report} state={state} signedInAs={signedInAs} refreshing={view === 'refreshing'} />;

  // ⚠️ The brand comes from the PAYLOAD, resolved server-side from the scope —
  // the browser never picks it. Phase 1 passed `null` here, so every report
  // rendered kyck-default regardless of whose it was.
  const config = resolvePortalReportConfig(report.brandAccountId, report.brandName);
  const block = (which: 'combined' | 'google' | 'meta') => () => (
    <ChannelBlock
      block={report[which]}
      labels={report.labels}
      grouping={state.grouping}
      onGroupingChange={state.setGrouping}
    />
  );

  // ⚠️⚠️ THE `google` RENDERER IS NOT REGISTERED when Google is suppressed, and
  // `'google'` is dropped from the section ORDER below. `ReportSections` SKIPS a
  // section id with no renderer (that is what makes the `crm` slot free), so
  // either alone would work — both are done because the order is what a future
  // per-brand config edits, and a registered renderer sitting behind a filtered
  // order is a Google block one config change away from reappearing.
  const renderers: SectionRenderers = {
    combined: block('combined'),
    ...(report.showsGoogle ? { google: block('google') } : {}),
    meta: block('meta'),
    byLocation: () =>
      report.byLocation ? (
        <ByLocationTable
          rows={report.byLocation}
          labels={report.labels}
          onSelect={state.setAccountId}
        />
      ) : null,
    // ⚠️ The `crm` slot, filled 2026-08-31 — a renderer and a config entry, with
    // no change to this page's shape, which is exactly what the placeholder was
    // for. It renders only when the payload carries a block: a scope with no
    // connected club sends null and the section is then ABSENT rather than empty.
    crm: () =>
      report.crm ? (
        <CrmSection crm={report.crm} labels={report.labels} range={report.range} />
      ) : null,
  };

  return (
    <ReportLayout
      state={state}
      signedInAs={signedInAs}
      accounts={report.accounts}
      selectedAccountId={report.selectedAccountId}
      showsGoogle={report.showsGoogle}
    >
      <ReportSections
        order={config.overviewSections.filter((s) => report.showsGoogle || s !== 'google')}
        renderers={renderers}
      />
    </ReportLayout>
  );
}

// ---------------------------------------------------------------------------

export function ReportMetaPage() {
  const state = useReportState();
  const signedInAs = firebaseAuth.currentUser?.email ?? null;
  const query = useQuery<PortalMetaReport>({
    queryKey: ['portal-report-meta', state.params],
    queryFn: () => fetchPortalMetaReport(state.params!),
    enabled: state.params !== null,
    // ⚠️⚠️ KEEP THE PREVIOUS REPORT WHILE A NEW RANGE IS PICKED (§2). A key
    // change would otherwise empty `query.data`, and the chrome — including the
    // period control — is rendered from the report. Picking "Custom range" makes
    // `params` null, so without this the page has nothing to draw and the control
    // that supplies the dates goes with it.
    //
    // ⚠️ THE HEADER STAYS HONEST FOR FREE: it renders `report.range`, the
    // PAYLOAD's window, so a half-picked range keeps naming the period the
    // numbers actually came from rather than the one being drafted.
    placeholderData: (prev) => prev,
    retry: false,
  });

  // ⚠️⚠️ ONE RULE, ONE PLACE (§2, `resolveReportView`). This was three copies of
  // a guard trio agreeing by coincidence, and the copy that mattered read
  // `params === null` — a sentinel meaning "do not fetch yet" — as "nothing to
  // show", which removed the control the page was waiting for.
  const view = resolveReportView({
    paramsReady: state.params !== null,
    hasReport: Boolean(query.data),
    isLoading: query.isLoading,
    hasError: Boolean(query.error),
    // ⚠️⚠️ THE NARROW PAIR, NOT `isFetching` ALONE (§8). `isPlaceholderData` means
    // "what is on screen came from the PREVIOUS key" — which is exactly the case
    // where the figures and the controls disagree. A same-key background refetch
    // (a window refocus) is fetching too, and flashing a cue over data that is
    // already correct is how a reader learns to ignore it.
    isRefreshing: query.isFetching && query.isPlaceholderData,
  });
  if (view === 'pick-dates') return <PickDates state={state} signedInAs={signedInAs} />;
  if (view === 'loading') return <Loading />;
  if (view === 'error' || !query.data) return <ReportError error={query.error} />;

  const report = query.data;
  if (report.accounts.length === 0) return <EmptyScope state={state} signedInAs={signedInAs} />;
  // ⚠️ Always true for a non-empty scope — see the Overview page above.
  // Everything below is the retained revert path, not live code.
  if (report.restyledReport) return <Restyled tab="meta" meta={report} state={state} signedInAs={signedInAs} refreshing={view === 'refreshing'} />;
  const config = resolvePortalReportConfig(report.brandAccountId, report.brandName);

  const renderers: SectionRenderers = {
    meta: () => (
      <ChannelBlock
        block={report.block}
        labels={report.labels}
        grouping={state.grouping}
        onGroupingChange={state.setGrouping}
      />
    ),
    byLocation: () =>
      report.byLocation ? (
        <ByLocationTable
          rows={report.byLocation}
          labels={report.labels}
          onSelect={state.setAccountId}
        />
      ) : null,
    campaigns: () => <CampaignsTable rows={report.campaigns} labels={report.labels} />,
    creatives: () => <CreativesGrid rows={report.creatives} labels={report.labels} />,
  };

  return (
    <ReportLayout
      state={state}
      signedInAs={signedInAs}
      accounts={report.accounts}
      selectedAccountId={report.selectedAccountId}
      campaignOptions={report.campaignOptions}
      // ⚠️ The META page needs it too: the nav is shared, so a suppressed
      // channel must be absent from every page's tab strip, not just the
      // Overview's.
      showsGoogle={report.showsGoogle}
    >
      <ReportSections order={config.metaSections} renderers={renderers} />
    </ReportLayout>
  );
}

// ---------------------------------------------------------------------------

export function ReportGooglePage() {
  const state = useReportState();
  const signedInAs = firebaseAuth.currentUser?.email ?? null;
  const query = useQuery<PortalGoogleReport>({
    queryKey: ['portal-report-google', state.params],
    queryFn: () => fetchPortalGoogleReport(state.params!),
    enabled: state.params !== null,
    // ⚠️⚠️ KEEP THE PREVIOUS REPORT WHILE A NEW RANGE IS PICKED (§2). A key
    // change would otherwise empty `query.data`, and the chrome — including the
    // period control — is rendered from the report. Picking "Custom range" makes
    // `params` null, so without this the page has nothing to draw and the control
    // that supplies the dates goes with it.
    //
    // ⚠️ THE HEADER STAYS HONEST FOR FREE: it renders `report.range`, the
    // PAYLOAD's window, so a half-picked range keeps naming the period the
    // numbers actually came from rather than the one being drafted.
    placeholderData: (prev) => prev,
    retry: false,
  });

  // ⚠️⚠️ ONE RULE, ONE PLACE (§2, `resolveReportView`). This was three copies of
  // a guard trio agreeing by coincidence, and the copy that mattered read
  // `params === null` — a sentinel meaning "do not fetch yet" — as "nothing to
  // show", which removed the control the page was waiting for.
  const view = resolveReportView({
    paramsReady: state.params !== null,
    hasReport: Boolean(query.data),
    isLoading: query.isLoading,
    hasError: Boolean(query.error),
    // ⚠️⚠️ THE NARROW PAIR, NOT `isFetching` ALONE (§8). `isPlaceholderData` means
    // "what is on screen came from the PREVIOUS key" — which is exactly the case
    // where the figures and the controls disagree. A same-key background refetch
    // (a window refocus) is fetching too, and flashing a cue over data that is
    // already correct is how a reader learns to ignore it.
    isRefreshing: query.isFetching && query.isPlaceholderData,
  });
  if (view === 'pick-dates') return <PickDates state={state} signedInAs={signedInAs} />;
  if (view === 'loading') return <Loading />;
  if (view === 'error' || !query.data) return <ReportError error={query.error} />;

  const report = query.data;
  if (report.accounts.length === 0) return <EmptyScope state={state} signedInAs={signedInAs} />;
  // ⚠️⚠️ THE ROUTE MUST NOT RENDER WHEN GOOGLE IS SUPPRESSED. The nav no longer
  // offers this tab, but a BOOKMARKED or FORWARDED link is exactly how someone
  // reaches a tab that is no longer there — and that is the case where "any
  // mention of Google" would otherwise leak a whole page.
  //
  // ⚠️ The redirect can only fire once the payload resolves, because this page
  // owns its own fetch and `showsGoogle` arrives with the data. A spinner then a
  // redirect is the accepted cost; there is nowhere earlier to know.
  //
  // ⚠️ `replace`, so the back button does not bounce the client straight back
  // into the page they were just redirected out of.
  if (!report.showsGoogle) return <Navigate to="/portal/report" replace />;
  // ⚠️ Always true for a non-empty scope — see the Overview page above.
  // Everything below is the retained revert path, not live code.
  if (report.restyledReport) return <Restyled tab="google" google={report} state={state} signedInAs={signedInAs} refreshing={view === 'refreshing'} />;
  const config = resolvePortalReportConfig(report.brandAccountId, report.brandName);

  const renderers: SectionRenderers = {
    google: () => (
      <ChannelBlock
        block={report.block}
        labels={report.labels}
        grouping={state.grouping}
        onGroupingChange={state.setGrouping}
      />
    ),
    byLocation: () =>
      report.byLocation ? (
        <ByLocationTable
          rows={report.byLocation}
          labels={report.labels}
          onSelect={state.setAccountId}
        />
      ) : null,
    campaigns: () => <CampaignsTable rows={report.campaigns} labels={report.labels} />,
    searchTerms: () => (
      <SearchTermsTable
        rows={report.searchTerms}
        labels={report.labels}
        capped={report.searchTermsCapped}
        total={report.searchTermsTotal}
      />
    ),
  };

  return (
    <ReportLayout
      state={state}
      signedInAs={signedInAs}
      accounts={report.accounts}
      selectedAccountId={report.selectedAccountId}
      campaignOptions={report.campaignOptions}
    >
      <ReportSections order={config.googleSections} renderers={renderers} />
    </ReportLayout>
  );
}

/**
 * Website Analytics (GA4). Restyled path only — there is no pre-restyle version
 * of this page and no revert path to keep.
 *
 * ⚠️ The studio picker lives in the URL (`?studio=`) so a forwarded link opens
 * the same studio. `carryAcrossTabChange` is an allowlist that does not list it,
 * so leaving this tab drops it — it means nothing on the other three.
 * ⚠️ The picker only REQUESTS a studio. The server resolves what this scope may
 * see, and a studio outside it is a 404, which renders as the error page.
 */
export function ReportWebsitePage() {
  const state = useReportState();
  const signedInAs = firebaseAuth.currentUser?.email ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const studio = searchParams.get('studio');
  const query = useQuery<PortalWebsiteReport>({
    queryKey: ['portal-report-website', state.params, studio],
    queryFn: () => fetchPortalWebsiteReport({ ...state.params!, studio }),
    enabled: state.params !== null,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const view = resolveReportView({
    paramsReady: state.params !== null,
    hasReport: Boolean(query.data),
    isLoading: query.isLoading,
    hasError: Boolean(query.error),
    isRefreshing: query.isFetching && query.isPlaceholderData,
  });
  if (view === 'pick-dates') return <PickDates state={state} signedInAs={signedInAs} />;
  if (view === 'loading') return <Loading />;
  if (view === 'error' || !query.data) return <ReportError error={query.error} />;
  const report = query.data;
  if (report.accounts.length === 0) return <EmptyScope state={state} signedInAs={signedInAs} />;
  // ⚠️ Same posture as the Google page: a bookmarked link to a tab this scope
  // does not have goes back to the Overview rather than rendering.
  if (!report.showsWebsite) return <Navigate to="/portal/report" replace />;
  const onStudioChange = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('studio', next);
    else params.delete('studio');
    setSearchParams(params);
  };
  // ⚠️ Gated on the flag like the other three, so the revert stays uniform. The
  // legacy layout has NO website page, so a flag-off scope goes to the Overview
  // rather than to a page that does not exist in that presentation.
  if (report.restyledReport) return <Restyled tab="website" website={report} onStudioChange={onStudioChange} state={state} signedInAs={signedInAs} refreshing={view === 'refreshing'} />;
  return <Navigate to="/portal/report" replace />;
}

/**
 * The ClubWise data tab (Anytime Fitness). Restyled path only, like Website
 * Analytics. It reads the SAME Overview payload (the ClubWise detail rides on its
 * CRM block), so it shares that page's query key and costs no second request.
 *
 * Gated on the payload's own CRM block, never on the browser: a scope without a
 * ClubWise club goes back to the Overview.
 */
export function ReportClubwisePage() {
  const state = useReportState();
  const signedInAs = firebaseAuth.currentUser?.email ?? null;
  const query = useQuery<PortalOverviewReport>({
    queryKey: ['portal-report-overview', state.params],
    queryFn: () => fetchPortalOverview(state.params!),
    enabled: state.params !== null,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const view = resolveReportView({
    paramsReady: state.params !== null,
    hasReport: Boolean(query.data),
    isLoading: query.isLoading,
    hasError: Boolean(query.error),
    isRefreshing: query.isFetching && query.isPlaceholderData,
  });
  if (view === 'pick-dates') return <PickDates state={state} signedInAs={signedInAs} />;
  if (view === 'loading') return <Loading />;
  if (view === 'error' || !query.data) return <ReportError error={query.error} />;
  const report = query.data;
  if (report.accounts.length === 0) return <EmptyScope state={state} signedInAs={signedInAs} />;
  if (!report.crm?.clubwise) return <Navigate to="/portal/report" replace />;
  if (report.restyledReport) return <Restyled tab="clubwise" overview={report} state={state} signedInAs={signedInAs} refreshing={view === 'refreshing'} />;
  return <Navigate to="/portal/report" replace />;
}
